import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { findFirstAccessibleFloor, useStableRawSites } from './trees/shared';
import { getActiveModuleKey } from './ModuleSwitcher';
import { updateUrlParams } from './utils';

/**
 * Sole owner of the default-landing URL redirect for `/`, `/monitor`, and
 * `/studio`. Mounted by `<Sidebar />` so it runs regardless of the
 * sidebar's presentation state (expanded / collapsed / Settings / Connect)
 * — collapsed-sidebar users still get redirected to real data.
 *
 * 1. Monitor / Studio with no `spaceId` and no `buildingId` → first
 *    accessible landing (floor, optionally narrowed to a room/zone when
 *    the user's grant is leaf-only).
 * 2. Studio with `buildingId` but no `spaceId` → first accessible floor
 *    under that building.
 */
export function DefaultSpaceRedirector() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const module = getActiveModuleKey(pathname);

  // Pull URL params at render time so the lookups below depend on stable
  // string scalars rather than a freshly-allocated `URLSearchParams` each
  // render — keeps the dep arrays clean and re-runs deterministic.
  const { wantedSiteId, wantedBuildingId, currentSpaceId } = useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      wantedSiteId: params.get('siteId'),
      wantedBuildingId: params.get('buildingId'),
      currentSpaceId: params.get('spaceId'),
    };
  }, [search]);

  // Single `useStableRawSites` call (one Apollo subscriber + one
  // fingerprint memo inside this component) feeding both lookups below.
  // `SidebarMenu` mounts a second subscriber via `useFilteredSites`;
  // Apollo dedupes the network request between them, and each consumer
  // pays its own O(n) fingerprint pass over the same cached data.
  // `findFirstAccessibleFloor` is a pure walk, so each memo only re-runs
  // when its specific inputs (rawSites, hasAccess, per-lookup option)
  // change.
  const { rawSites, error: sitesError } = useStableRawSites();
  const { hasAccess } = useSpaceAccess();

  // Surface Apollo failures via Datadog so a collapsed-sidebar user (who
  // never sees the `SidebarMenu` error UI) doesn't sit on an empty page
  // with no telemetry trail. `addError` deduplicates by error identity,
  // so an effect-driven dispatch is fine — we don't need to debounce.
  useEffect(() => {
    if (!sitesError) return;
    datadogRum.addError(sitesError, { source: 'default-space-redirector' });
  }, [sitesError]);

  // Two scoped lookups, each a short-circuit walk over the raw site tree:
  //   • `defaultFloor` — first accessible landing anywhere, with `siteId`
  //     honoured as a preference so `/monitor?siteId=X` lands under site X.
  //   • `studioFloor` — first accessible floor scoped to a specific building
  //     (Studio's `?buildingId=…` resolver).
  // Going through `findFirstAccessibleFloor` directly (rather than
  // `useFilteredSites`) keeps the redirector off the room/zone
  // access-filter + name-filter + natural-sort pipeline, which would
  // otherwise re-run on every Apollo cache write to find one floor.
  const defaultFloor = useMemo(
    () => findFirstAccessibleFloor(rawSites, hasAccess, { preferSiteId: wantedSiteId }),
    [rawSites, hasAccess, wantedSiteId],
  );
  // `studioFloor` only resolves when the URL carries a building scope —
  // the building-scoped walk shares no work with the open lookup above,
  // so short-circuiting to null avoids duplicating `defaultFloor`'s walk
  // on every non-Studio page.
  const studioFloor = useMemo(
    () =>
      wantedBuildingId
        ? findFirstAccessibleFloor(rawSites, hasAccess, { buildingId: wantedBuildingId })
        : null,
    [rawSites, hasAccess, wantedBuildingId],
  );

  // Default-landing redirect for Monitor / Studio when the URL carries no
  // scope params. `siteId` is intentionally NOT in the early-return guard
  // because Monitor / Studio don't read it (it's a Devices-only filter) —
  // gating on it would strand users on `/monitor?siteId=X` with an empty
  // dashboard. The lookup above honours `siteId` as a *preference* so a
  // shared URL still lands under the intended site when one is reachable.
  //
  // When `defaultFloor.roomId` / `defaultFloor.zoneId` is set the user's
  // only grant at the chosen floor is via that leaf — narrowing the URL
  // to the leaf is required for `SpaceAccessGuard` to let the user
  // through (a bare `?spaceId=…` for a floor they have no floor-level
  // grant on would be rejected).
  //
  // `replace: true` keeps history clean so Back doesn't bounce back to
  // the bare URL. `siteId` is consumed as a one-shot preference and
  // dropped from the post-redirect URL — Monitor / Studio don't read it,
  // and lingering produces mixed state (`?spaceId=F&siteId=X`) that
  // `DeviceManagementPage` (which DOES read `siteId`) would
  // misinterpret on cross-module navigation.
  useEffect(() => {
    // `<Sidebar />` (and therefore this redirector) is also mounted on
    // `/settings/*` and `/connect/*`. Those routes don't read `spaceId`,
    // but writing one would pollute their URLs (`/settings/profile?spaceId=…`)
    // and contradict this file's stated invariant — it owns the landing
    // redirect for `/`, `/monitor`, and `/studio` only. `getActiveModuleKey`
    // falls back to `monitor` for anything it doesn't recognise, which is
    // why a positive `module` check isn't enough here.
    if (pathname.startsWith('/settings') || pathname.startsWith('/connect')) return;
    if (module === 'devices') return;
    if (currentSpaceId || wantedBuildingId) return;
    if (!defaultFloor) return;
    const params = new URLSearchParams(search);
    params.delete('siteId');
    params.set('spaceId', defaultFloor.floorId);
    if (defaultFloor.roomId) params.set('roomId', defaultFloor.roomId);
    if (defaultFloor.zoneId) params.set('zoneId', defaultFloor.zoneId);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  }, [pathname, module, currentSpaceId, wantedBuildingId, defaultFloor, navigate, search]);

  // Studio-specific: when the URL has `?buildingId=…` but no `?spaceId=…`,
  // resolve to the first accessible floor under that building. The
  // `currentSpaceId` guard handles the bail cases naturally: once we
  // redirect, `updateUrlParams` writes `?spaceId=…` which clears
  // `buildingId` (mutually exclusive), so the next dep-change sees no
  // buildingId and bails. Subsequent cache writes also see `spaceId` set
  // and bail. No "done ref" needed — a ref would latch true and strand
  // the user if they browser-back to a different `?buildingId=…`.
  // `replace: true` keeps Back from bouncing through the intermediate
  // buildingId-only URL.
  useEffect(() => {
    if (module !== 'studio') return;
    if (!wantedBuildingId || currentSpaceId) return;
    if (!studioFloor) return;
    updateUrlParams(navigate, { floorId: studioFloor.floorId }, { replace: true });
  }, [module, wantedBuildingId, currentSpaceId, studioFloor, navigate]);

  return null;
}
